import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { navLinks, servicesDropdown } from "../../data/mockData";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";

const Navbar = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [auditButtonHovered, setAuditButtonHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  }, [location]);

  return (
    <>
      <style>{`
        @keyframes navbar-btn-glow {
          0%, 100% { box-shadow: 0 0 15px rgba(27, 27, 58, 0.2), 0 4px 15px rgba(27, 27, 58, 0.1); }
          50% { box-shadow: 0 0 25px rgba(27, 27, 58, 0.4), 0 6px 20px rgba(27, 27, 58, 0.15); }
        }
        @keyframes navbar-btn-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.01); }
        }
      `}</style>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-white shadow-md py-3" : "bg-white py-5"
        }`}
      >
        <div className="max-w-[1320px] mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1 no-underline flex-shrink-0">
            <img 
              src="/Logo/Jpg/urban%20hunter%20logo%20icon-01.jpg" 
              alt="Urban Hunter Logo"
              className="h-24 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.name === "Services" ? (
                <div
                  key={link.path}
                  className="relative group"
                  onMouseEnter={() => setServicesDropdownOpen(true)}
                  onMouseLeave={() => setServicesDropdownOpen(false)}
                >
                  <Link
                    to="/services"
                    className={`text-[15px] font-medium transition-colors duration-200 no-underline relative pb-1 flex items-center gap-1 ${
                      location.pathname.includes("/services")
                        ? "text-[#10B981]"
                        : "text-[#1B1B3A] hover:text-[#10B981]"
                    }`}
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {link.name}
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-300 ${
                        servicesDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                    {location.pathname.includes("/services") && (
                      <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#10B981]" />
                    )}
                  </Link>

                  {/* Desktop Dropdown Menu */}
                  <div
                    className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[700px] bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transition-all duration-300 origin-top ${
                      servicesDropdownOpen
                        ? "opacity-100 visible scale-y-100"
                        : "opacity-0 invisible scale-y-95"
                    }`}
                  >
                    <div className="grid grid-cols-2 gap-0 p-6">
                      {servicesDropdown.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          className="px-4 py-4 hover:bg-[#F8F6FF] transition-colors duration-200 no-underline rounded-lg"
                        >
                          <div
                            className="text-[14px] font-semibold text-[#1B1B3A] hover:text-[#10B981]"
                            style={{ fontFamily: "'Poppins', sans-serif" }}
                          >
                            {service.title}
                          </div>
                          <div
                            className="text-[12px] text-[#888] mt-1"
                            style={{ fontFamily: "'Poppins', sans-serif" }}
                          >
                            {service.description}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : link.path === "#" ? (
                <button
                  key={link.path}
                  className={`text-[15px] font-medium transition-colors duration-200 no-underline relative pb-1 bg-none border-none cursor-pointer ${
                    location.pathname === link.path
                        ? "text-[#10B981]"
                        : "text-[#1B1B3A] hover:text-[#10B981]"
                  }`}
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {link.name}
                </button>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-[15px] font-medium transition-colors duration-200 no-underline relative pb-1 ${
                    location.pathname === link.path
                        ? "text-[#10B981]"
                        : "text-[#1B1B3A] hover:text-[#10B981]"
                  }`}
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {link.name}
                  {location.pathname === link.path && (
                    <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#10B981]" />
                  )}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button */}
          <Link
            to="/free-audit"
            onMouseEnter={() => setAuditButtonHovered(true)}
            onMouseLeave={() => setAuditButtonHovered(false)}
            className="hidden md:flex items-center gap-2 bg-[#1B1B3A] text-white px-6 py-3 rounded-full text-[14px] font-semibold tracking-wider uppercase no-underline transition-all duration-300"
            style={{ 
              fontFamily: "'Poppins', sans-serif",
              transform: auditButtonHovered ? "translateY(-2px) scale(1.03)" : "translateY(0) scale(1)",
              boxShadow: auditButtonHovered 
                ? "0 0 20px rgba(27, 27, 58, 0.4), 0 6px 20px rgba(27, 27, 58, 0.15)"
                : "0 0 15px rgba(27, 27, 58, 0.2), 0 4px 15px rgba(27, 27, 58, 0.1)",
              animation: !auditButtonHovered ? "navbar-btn-pulse 3s ease-in-out infinite" : "none"
            }}
          >
            Free Audit
            <ArrowRight 
              size={16}
              style={{
                transition: "transform 0.3s ease",
                transform: auditButtonHovered ? "translateX(4px)" : "translateX(0)"
              }}
            />
          </Link>

        {/* Mobile menu button */}
        <button
          className="md:hidden text-[#1B1B3A] p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-6 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              link.name === "Services" ? (
                <div key={link.path} className="flex flex-col gap-2">
                  <button
                    onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                    className={`text-[15px] font-medium no-underline py-2 flex items-center justify-between ${
                      location.pathname.includes("/services")
                        ? "text-[#10B981]"
                        : "text-[#1B1B3A]"
                    }`}
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {link.name}
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-300 ${
                        servicesDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {servicesDropdownOpen && (
                    <div className="flex flex-col gap-2 pl-4 border-l-2 border-[#10B981]">

                      {servicesDropdown.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          onClick={() => {
                            setServicesDropdownOpen(false);
                            setMobileMenuOpen(false);
                          }}
                          className="text-[14px] font-medium text-[#555] hover:text-[#10B981] no-underline py-2 transition-colors"
                          style={{ fontFamily: "'Poppins', sans-serif" }}
                        >
                          {service.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : link.path === "#" ? (
                <button
                  key={link.path}
                  className={`text-[15px] font-medium no-underline py-2 bg-none border-none cursor-pointer w-full text-left ${
                    location.pathname === link.path
                      ? "text-[#10B981]"
                      : "text-[#1B1B3A]"
                  }`}
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {link.name}
                </button>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-[15px] font-medium no-underline py-2 ${
                    location.pathname === link.path
                      ? "text-[#10B981]"
                      : "text-[#1B1B3A]"
                  }`}
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {link.name}
                </Link>
              )
            ))}
            <Link
              to="/free-audit"
              className="flex items-center justify-center gap-2 bg-[#1B1B3A] text-white px-6 py-3 rounded-full text-[14px] font-semibold tracking-wider uppercase no-underline mt-2"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Free Audit
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navbar;
